import { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/core/auth/AuthContext';
import { usePermission } from '@/core/permissions';
import { agroProductListService } from '../services/agroProductList.service';
import type { AgroProductFilters } from '../types/agroProductList.types';
import type { AgroProductListItem } from '../types/agroProductList.types';

export function useAgroProductList() {
  const { profile } = useAuth();
  const { can, isAdmin } = usePermission();
  const companyId = profile?.company_id || '';
  const userId = profile?.id || '';
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<AgroProductFilters>({
    search: '',
    status: 'all',
    groupId: '',
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const query = useQuery({
    queryKey: ['agro', 'list', companyId, filters, page, pageSize],
    queryFn: () => agroProductListService.getAgroProductList(companyId, filters),
    enabled: !!companyId,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => agroProductListService.deleteAgroProductAsset(id, companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agro', 'list'] });
      toast.success('Đã xóa vật tư thành công');
    },
    onError: (err: Error) => toast.error(`Lỗi khi xóa: ${err.message}`),
  });

  const groupsQuery = useQuery({
    queryKey: ['agro', 'groups', companyId],
    queryFn: () => agroProductListService.getGroups(companyId),
    enabled: !!companyId,
  });

  const allItems = query.data?.items ?? [];
  const from = (page - 1) * pageSize;
  const pagedItems = allItems.slice(from, from + pageSize);

  const handleSearch = (search: string) => {
    setFilters(prev => ({ ...prev, search }));
    setPage(1);
  };

  const handleStatusChange = (status: any) => {
    setFilters(prev => ({ ...prev, status }));
    setPage(1);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const filterOptions = useMemo(() => {
    const rawData = groupsQuery.data || [];
    const childrenMap = new Map<string, any[]>();
    const roots: any[] = [];

    rawData.forEach((item: any) => {
      if (!item.parent_id) {
        roots.push(item);
      } else {
        if (!childrenMap.has(item.parent_id)) {
          childrenMap.set(item.parent_id, []);
        }
        childrenMap.get(item.parent_id)!.push(item);
      }
    });

    const flattened: { label: string, value: string }[] = [];

    const traverse = (items: any[], prefix: string, level: number) => {
      items.forEach((item, index) => {
        const seq = prefix ? `${prefix}.${index + 1}` : `${index + 1}`;
        const indent = level > 1 ? '\u00A0\u00A0\u00A0\u00A0'.repeat(level - 1) : '';
        flattened.push({
          label: `${indent}${seq}. ${item.name}`,
          value: item.id
        });
        
        const children = childrenMap.get(item.id);
        if (children && children.length > 0) {
          traverse(children, seq, level + 1);
        }
      });
    };

    traverse(roots, '', 1);

    return { groups: flattened };
  }, [groupsQuery.data]);

  return {
    data: {
      items: pagedItems,
      total: allItems.length,
      stats: query.data?.stats ?? { totalAssets: 0, availableCount: 0, outOfStockCount: 0, expiredCount: 0, utilizationRate: 0 },
    },
    isLoading: query.isLoading,
    isDeleting: deleteMutation.isPending,
    filters,
    filterOptions,
    page,
    pageSize,
    canEdit: isAdmin || can('agro', 'update'),
    canDelete: isAdmin || can('agro', 'delete'),
    handleSearch,
    handleStatusChange,
    handleFilterChange,
    deleteItem: deleteMutation.mutateAsync,
    updatePage: setPage,
    updatePageSize: (size: number) => { setPageSize(size); setPage(1); },
  };
}
